import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, MessageSquare, Search, Calendar, MessageCircle, MapPin, Shield, ChevronDown, Home, Info, GraduationCap, HelpCircle, LayoutDashboard, Settings, Mail, LogOut, User, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const LOGO_URL = "/logo.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nearbyCount, setNearbyCount] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user && user.subscription_tier !== 'free') {
      const fetchUnread = async () => {
        try {
          const { data } = await api.get('/messages/unread-count');
          setUnreadCount(data.unread_count || 0);
        } catch (err) { console.debug('unread-count failed:', err?.message); }
      };
      fetchUnread();
      const interval = setInterval(fetchUnread, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Poll nearby game requests so the Dashboard dropdown link can show a red
  // dot when there's something to act on. Backend gates by tier — free
  // accounts always come back empty so the dot never lights up for them.
  useEffect(() => {
    if (!user || user.is_demo) return undefined;
    const fetchNearby = async () => {
      try {
        const { data } = await api.get('/game-requests/nearby');
        setNearbyCount((data.requests || []).length);
      } catch (err) { console.debug('nearby game-requests failed:', err?.message); }
    };
    fetchNearby();
    const interval = setInterval(fetchNearby, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate('/');
  };

  const publicLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/events', label: 'Events' },
    { to: '/forum', label: 'Discussion Board' },
    { to: '/instructors', label: 'Instructors' },
    { to: '/play-locations', label: 'Play Locations' },
    { to: '/pricing', label: 'Membership' },
    { to: '/faq', label: 'FAQ' },
    { to: '/contact', label: 'Contact' },
  ];

  // Logged-in top nav tabs (left to right). Refer & Win sits in the
  // dropdown rather than the top row to keep the bar uncluttered on
  // ~1280px laptops, but mobile renders the same list so members see
  // it on every viewport.
  const memberTabs = [
    { to: '/find-players', label: 'Find Players', icon: Search },
    { to: '/play-locations', label: 'Play Locations', icon: MapPin },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/forum', label: 'Discussion Board', icon: MessageCircle },
    { to: '/messages', label: 'Messages', icon: MessageSquare, badge: unreadCount },
  ];

  // Profile dropdown items (top to bottom)
  // 'My Profile' goes first — caught 24 Jun 2026 when a new user
  // emailed: "I need to edit a spelling error from my bio and can't
  // find where to edit that." The menu didn't surface /profile at
  // all, so users had no breadcrumb to their editable profile.
  // Profile-edit pulse: nudges first-time users toward the avatar
  // menu so they discover "My Profile" → bio editor without emailing
  // support. localStorage key is per-user so a second device shows
  // the pulse once too, but is permanently dismissed on first click.
  const profileNudgeKey = user ? `mfm.profile_nudge_seen.${user.user_id || user.email}` : null;
  const [showProfileNudge, setShowProfileNudge] = useState(false);
  useEffect(() => {
    if (!user || !profileNudgeKey) return;
    try {
      const seen = window.localStorage.getItem(profileNudgeKey);
      if (!seen) setShowProfileNudge(true);
    } catch (_e) { /* localStorage unavailable — skip nudge */ }
  }, [user, profileNudgeKey]);
  const dismissProfileNudge = () => {
    setShowProfileNudge(false);
    try {
      if (profileNudgeKey) window.localStorage.setItem(profileNudgeKey, '1');
    } catch (_e) { /* ignore */ }
  };

  const profileDropdownLinks = [
    { to: '/profile', label: 'My Profile', icon: User, highlight: true },
    { to: '/', label: 'Home', icon: Home },
    { to: '/about', label: 'About', icon: Info },
    { to: '/instructors', label: 'Instructors', icon: GraduationCap },
    { to: '/referrals', label: 'Refer & Earn', icon: Trophy },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, dot: nearbyCount > 0 },
    { to: '/settings', label: 'Settings', icon: Settings },
    { to: '/contact', label: 'Contact', icon: Mail },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Meet For Mahjong" className="h-9 w-auto" />
            <span className="font-bold text-lg hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
              Meet For Mahjong
            </span>
          </Link>

          {/* Desktop Nav — switches on at `xl` (1280px) instead of
              `lg` (1024px). The logged-in tab row + auth buttons
              measure ~1150px wide, so `lg` triggered a horizontal
              overflow of ~40-100px on Mac laptops at 1024-1279
              effective width (including any Mac user using ⌘+ zoom).
              Caught 27 Jul 2026 — user emailed: "The website text is
              much too wide to view on a mac. People who own macs
              have to scroll back and forth." */}
          <div className="hidden xl:flex items-center gap-1">
            {!user ? (
              <>
                {publicLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-3 py-2 text-sm rounded-full transition-all duration-300 ${
                      isActive(link.to)
                        ? 'bg-[#56dfb4]/15 text-[#0f172a] font-medium'
                        : 'text-slate-600 hover:text-[#0f172a] hover:bg-slate-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="ml-3 flex gap-2">
                  <Link to="/login">
                    <Button variant="outline" className="rounded-full text-sm border-2 border-slate-300 hover:border-[#56dfb4] hover:text-[#0f172a]" data-testid="nav-login-btn">Log In</Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      className="rounded-full text-white text-sm font-semibold border-0 hover:brightness-110 transition-all"
                      style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}
                      data-testid="nav-register-btn"
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* Main nav tabs */}
                {memberTabs.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-3 py-2 text-sm rounded-full transition-all duration-300 flex items-center gap-1.5 ${
                      isActive(link.to)
                        ? 'bg-[#56dfb4]/15 text-[#0f172a] font-medium'
                        : 'text-slate-600 hover:text-[#0f172a] hover:bg-slate-50'
                    }`}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                    {link.badge > 0 && (
                      <span className="ml-1 bg-[#df5681] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {link.badge > 9 ? '9+' : link.badge}
                      </span>
                    )}
                  </Link>
                ))}
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className={`px-3 py-2 text-sm rounded-full transition-all duration-300 flex items-center gap-1.5 ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-[#b456df]/15 text-[#b456df] font-medium'
                        : 'text-slate-600 hover:text-[#b456df] hover:bg-slate-50'
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </Link>
                )}

                {/* Profile dropdown */}
                <div className="ml-3 relative" ref={dropdownRef}>
                  <button
                    onClick={() => { setProfileOpen(!profileOpen); dismissProfileNudge(); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-slate-50 transition-all"
                    data-testid="nav-profile-dropdown"
                  >
                    <div className="relative w-8 h-8 rounded-full bg-[#56dfb4]/20 flex items-center justify-center text-sm font-medium text-[#0f172a]">
                      {(user.display_name || user.first_name || 'U')[0].toUpperCase()}
                      {nearbyCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#df5681] ring-2 ring-white animate-pulse" aria-label="Nearby game requests" data-testid="nav-profile-dot" />
                      )}
                      {showProfileNudge && nearbyCount === 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#b456df] ring-2 ring-white animate-pulse" aria-label="Edit your profile" data-testid="nav-profile-nudge-dot" />
                      )}
                    </div>
                    <span className="text-sm font-medium hidden xl:block">{user.display_name || user.first_name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* First-time nudge tooltip — points at the avatar
                      with a friendly "Edit your profile here" hint.
                      Auto-dismisses on first click of the avatar. */}
                  {showProfileNudge && !profileOpen && (
                    <div
                      className="absolute right-0 top-full mt-3 w-64 bg-[#0f172a] text-white text-xs rounded-xl shadow-xl px-3.5 py-2.5 z-50"
                      data-testid="nav-profile-nudge-tooltip"
                    >
                      <div className="absolute -top-1.5 right-6 w-3 h-3 bg-[#0f172a] rotate-45" />
                      <div className="flex items-start gap-2">
                        <User className="w-3.5 h-3.5 text-[#56dfb4] mt-0.5 shrink-0" />
                        <div className="flex-1 leading-snug">
                          Tap your avatar to edit your profile, bio, or settings →
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); dismissProfileNudge(); }}
                          className="text-slate-400 hover:text-white ml-1"
                          aria-label="Dismiss"
                          data-testid="nav-profile-nudge-dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {profileOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-slate-200 shadow-lg py-1.5 z-50" data-testid="profile-dropdown-menu">
                      {profileDropdownLinks.map(link => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={() => setProfileOpen(false)}
                          className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                            isActive(link.to)
                              ? 'bg-[#56dfb4]/10 text-[#0f172a] font-medium'
                              : link.highlight
                                ? 'text-[#0f172a] font-medium hover:bg-[#56dfb4]/10'
                                : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          data-testid={`dropdown-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <link.icon className={`w-4 h-4 ${link.highlight ? 'text-[#56dfb4]' : 'text-slate-400'}`} />
                          <span className="flex-1">{link.label}</span>
                          {link.dot && (
                            <span className="w-2 h-2 rounded-full bg-[#df5681] animate-pulse" aria-label="Nearby game requests" data-testid={`dropdown-${link.label.toLowerCase().replace(/\s/g, '-')}-dot`} />
                          )}
                        </Link>
                      ))}
                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
                          data-testid="nav-logout-btn"
                        >
                          <LogOut className="w-4 h-4" />
                          Log Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="xl:hidden flex items-center gap-2">
            {/* Persistent Log In on mobile so it's reachable in 1 tap, not 2 */}
            {!user && (
              <Link to="/login">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs border-2 border-slate-300 hover:border-[#56dfb4]"
                  data-testid="nav-mobile-login-btn"
                >
                  Log In
                </Button>
              </Link>
            )}
            <button
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-white border-b border-slate-200 animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            {!user ? (
              <>
                {publicLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-4 py-2.5 rounded-xl text-sm transition-colors ${
                      isActive(link.to) ? 'bg-[#56dfb4]/10 font-medium' : 'hover:bg-slate-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-3 flex gap-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full rounded-full">Log In</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button
                      className="w-full rounded-full text-white font-semibold border-0 hover:brightness-110 transition-all"
                      style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* Main tabs */}
                <p className="px-4 pt-1 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Navigate</p>
                {memberTabs.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                      isActive(link.to) ? 'bg-[#56dfb4]/10 font-medium' : 'hover:bg-slate-50'
                    }`}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                    {link.badge > 0 && (
                      <span className="ml-auto bg-[#df5681] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                ))}
                {user.role === 'admin' && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm hover:bg-slate-50">
                    <Shield className="w-4 h-4" />Admin
                  </Link>
                )}
                {/* Profile section */}
                <div className="border-t border-slate-100 mt-2 pt-2">
                  <p className="px-4 pt-1 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Account</p>
                  {profileDropdownLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                        isActive(link.to) ? 'bg-[#56dfb4]/10 font-medium' : 'hover:bg-slate-50'
                      }`}
                    >
                      <link.icon className="w-4 h-4 text-slate-400" />
                      <span className="flex-1">{link.label}</span>
                      {link.dot && (
                        <span className="w-2 h-2 rounded-full bg-[#df5681] animate-pulse" aria-label="Nearby game requests" />
                      )}
                    </Link>
                  ))}
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="w-full flex items-center gap-2 text-left px-4 py-2.5 rounded-xl text-sm text-slate-500 hover:bg-slate-50">
                    <LogOut className="w-4 h-4" />Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
